import React, { createContext, useState, useContext, useEffect } from "react"
import {
	getLanguageByCode,
	getCoursesForLanguage,
	getFlashcardsForLanguage,
	getLanguages,
} from "../firebase/services.js"
import {
	getAuth,
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signOut,
	onAuthStateChanged,
	updateProfile,
} from "firebase/auth"
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore"
import { db } from "../firebase/firebase"

// Create context
const FirebaseContext = createContext(null)

// Custom hook to use the Firebase context
export const useFirebase = () => useContext(FirebaseContext)

// Context provider component
export const FirebaseProvider = ({ children }) => {
	// State for all data from Firebase
	const [currentLanguage, setCurrentLanguage] = useState(null)
	const [currentCourses, setCurrentCourses] = useState([])
	const [currentFlashcards, setCurrentFlashcards] = useState([])
	const [availableLanguages, setAvailableLanguages] = useState([])
	const [isLoading, setIsLoading] = useState(true)
	const [languageCode, setLanguageCode] = useState(() => {
		return localStorage.getItem("hopLanguage") || "es"
	})

	// Auth state
	const [currentUser, setCurrentUser] = useState(null)
	const [authLoading, setAuthLoading] = useState(true)
	const [authError, setAuthError] = useState(null)
	const auth = getAuth()

	// Listen for auth state changes
	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, user => {
			setCurrentUser(user)
			setAuthLoading(false)
		})

		// Cleanup subscription
		return () => unsubscribe()
	}, [auth])

	// Fetch available languages on initial load
	useEffect(() => {
		const fetchLanguages = async () => {
			try {
				const languages = await getLanguages()
				setAvailableLanguages(languages)
			} catch (error) {
				console.error("Error fetching languages:", error)
			}
		}

		fetchLanguages()
	}, [])

	// Fetch data whenever language changes
	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true)
			try {
				// Get language data
				const language = await getLanguageByCode(languageCode)
				setCurrentLanguage(language)

				// Get courses
				const courses = await getCoursesForLanguage(languageCode)
				setCurrentCourses(courses)

				// Get flashcards
				const flashcards = await getFlashcardsForLanguage(languageCode)
				setCurrentFlashcards(flashcards)

				// Save language preference
				localStorage.setItem("hopLanguage", languageCode)
			} catch (error) {
				console.error("Error fetching data:", error)
			} finally {
				setIsLoading(false)
			}
		}

		fetchData()
	}, [languageCode])

	// Change language
	const changeLanguage = newLanguageCode => {
		setLanguageCode(newLanguageCode)
	}

	// Filter courses by search query
	const getFilteredCourses = searchQuery => {
		if (!searchQuery) return currentCourses

		return currentCourses.filter(course =>
			course.name.toLowerCase().includes(searchQuery.toLowerCase())
		)
	}

	// Auth methods
	const signUp = async (email, password, displayName) => {
		setAuthError(null)
		setAuthLoading(true)
		try {
			const userCredential = await createUserWithEmailAndPassword(
				auth,
				email,
				password
			)
			const user = userCredential.user

			// Update user profile with display name
			await updateProfile(user, { displayName })

			// Create a user document in Firestore
			await setDoc(doc(db, "users", user.uid), {
				displayName,
				email,
				createdAt: new Date(),
				streak: 0,
				learningGoal: "10 min/day",
				completedFlashcards: {},
				knownWords: {}, // Initialize empty known words object
				settings: {
					notifications: false,
					soundEffects: false,
				},
			})

			return user
		} catch (error) {
			console.error("Error signing up:", error)
			setAuthError(error.message)
			throw error
		} finally {
			setAuthLoading(false)
		}
	}

	const signIn = async (email, password) => {
		setAuthError(null)
		setAuthLoading(true)
		try {
			const userCredential = await signInWithEmailAndPassword(
				auth,
				email,
				password
			)
			return userCredential.user
		} catch (error) {
			console.error("Error signing in:", error)
			setAuthError(error.message)
			throw error
		} finally {
			setAuthLoading(false)
		}
	}

	const logOut = async () => {
		setAuthError(null)
		try {
			await signOut(auth)
		} catch (error) {
			console.error("Error signing out:", error)
			setAuthError(error.message)
			throw error
		}
	}

	// Get user data from Firestore
	const getUserData = async userId => {
		try {
			const userDoc = await getDoc(doc(db, "users", userId))
			if (userDoc.exists()) {
				return userDoc.data()
			}
			return null
		} catch (error) {
			console.error("Error getting user data:", error)
			return null
		}
	}

	// Update user streak in Firestore
	const updateUserStreak = async (userId, newStreak) => {
		try {
			const userRef = doc(db, "users", userId)
			await updateDoc(userRef, {
				streak: newStreak,
				lastStreakUpdate: new Date().toISOString().split("T")[0], // Store date as YYYY-MM-DD
			})
			return true
		} catch (error) {
			console.error("Error updating user streak:", error)
			return false
		}
	}

	// Update known words in Firestore
	const updateKnownWords = async (userId, languageCode, knownWordsArray) => {
		try {
			const userRef = doc(db, "users", userId)
			const userData = await getUserData(userId)

			const updatedKnownWords = {
				...userData?.knownWords,
				[languageCode]: knownWordsArray,
			}

			await updateDoc(userRef, {
				knownWords: updatedKnownWords,
				lastKnownWordUpdate: new Date().toISOString(),
			})
			return true
		} catch (error) {
			console.error("Error updating known words:", error)
			return false
		}
	}

	// Get known words for specific language
	const getKnownWords = async (userId, languageCode) => {
		try {
			const userData = await getUserData(userId)
			return userData?.knownWords?.[languageCode] || []
		} catch (error) {
			console.error("Error getting known words:", error)
			return []
		}
	}

	// Mark flashcard as known
	const markFlashcardAsKnown = async (flashcardId, languageCode) => {
		try {
			if (currentUser) {
				// For logged-in users, update Firebase
				const userData = await getUserData(currentUser.uid)
				const currentKnownWords = userData?.knownWords?.[languageCode] || []

				if (!currentKnownWords.includes(flashcardId)) {
					const updatedKnownWords = [...currentKnownWords, flashcardId]
					await updateKnownWords(
						currentUser.uid,
						languageCode,
						updatedKnownWords
					)
					return true
				}
			} else {
				// For guest users, update localStorage
				const storageKey = `hopKnownWords_${languageCode}`
				const currentKnownWords = JSON.parse(
					localStorage.getItem(storageKey) || "[]"
				)

				if (!currentKnownWords.includes(flashcardId)) {
					const updatedKnownWords = [...currentKnownWords, flashcardId]
					localStorage.setItem(storageKey, JSON.stringify(updatedKnownWords))
					return true
				}
			}
			return false
		} catch (error) {
			console.error("Error marking flashcard as known:", error)
			return false
		}
	}

	// Remove flashcard from known words
	const unmarkFlashcardAsKnown = async (flashcardId, languageCode) => {
		try {
			if (currentUser) {
				// For logged-in users, update Firebase
				const userData = await getUserData(currentUser.uid)
				const currentKnownWords = userData?.knownWords?.[languageCode] || []
				const updatedKnownWords = currentKnownWords.filter(
					id => id !== flashcardId
				)
				await updateKnownWords(currentUser.uid, languageCode, updatedKnownWords)
				return true
			} else {
				// For guest users, update localStorage
				const storageKey = `hopKnownWords_${languageCode}`
				const currentKnownWords = JSON.parse(
					localStorage.getItem(storageKey) || "[]"
				)
				const updatedKnownWords = currentKnownWords.filter(
					id => id !== flashcardId
				)
				localStorage.setItem(storageKey, JSON.stringify(updatedKnownWords))
				return true
			}
		} catch (error) {
			console.error("Error unmarking flashcard as known:", error)
			return false
		}
	}

	// Get learning statistics
	const getLearningStats = async languageCode => {
		try {
			const totalFlashcards = currentFlashcards.length
			let knownWordsCount = 0

			if (currentUser) {
				const knownWords = await getKnownWords(currentUser.uid, languageCode)
				knownWordsCount = knownWords.length
			} else {
				const storageKey = `hopKnownWords_${languageCode}`
				const knownWords = JSON.parse(localStorage.getItem(storageKey) || "[]")
				knownWordsCount = knownWords.length
			}

			const progressPercentage =
				totalFlashcards > 0
					? Math.round((knownWordsCount / totalFlashcards) * 100)
					: 0

			return {
				totalFlashcards,
				knownWordsCount,
				unknownWordsCount: totalFlashcards - knownWordsCount,
				progressPercentage,
			}
		} catch (error) {
			console.error("Error getting learning stats:", error)
			return {
				totalFlashcards: 0,
				knownWordsCount: 0,
				unknownWordsCount: 0,
				progressPercentage: 0,
			}
		}
	}

	// Context value
	const contextValue = {
		currentLanguage,
		currentCourses,
		currentFlashcards,
		availableLanguages,
		isLoading,
		changeLanguage,
		getFilteredCourses,
		// Auth values
		currentUser,
		authLoading,
		authError,
		signUp,
		signIn,
		logOut,
		getUserData,
		updateUserStreak,
		// Known words functionality
		updateKnownWords,
		getKnownWords,
		markFlashcardAsKnown,
		unmarkFlashcardAsKnown,
		getLearningStats,
	}

	return (
		<FirebaseContext.Provider value={contextValue}>
			{children}
		</FirebaseContext.Provider>
	)
}
